import { supabase } from "../../supabaseClient";

const APP_TIMEZONE = "Africa/Johannesburg";

function getTodayDateString() {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: APP_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  return formatter.format(new Date());
}

function ymdToUtcMs(ymd) {
  const [year, month, day] = String(ymd).split("-").map(Number);
  return Date.UTC(year, month - 1, day);
}

function diffDays(laterYmd, earlierYmd) {
  return Math.round((ymdToUtcMs(laterYmd) - ymdToUtcMs(earlierYmd)) / 86400000);
}

async function getCurrentUser() {
  const {
    data: { user },
    error,
  } = await supabase.auth.getUser();

  if (error) throw error;
  if (!user) throw new Error("Not authenticated");

  return user;
}

function normalizeCompletedLessons(value) {
  if (Array.isArray(value)) {
    return value.filter((x) => typeof x === "string" && x.trim().length > 0);
  }

  if (typeof value === "string" && value.trim()) {
    try {
      const parsed = JSON.parse(value);

      if (Array.isArray(parsed)) {
        return parsed.filter(
          (x) => typeof x === "string" && x.trim().length > 0
        );
      }
    } catch {
      return [];
    }
  }

  return [];
}

function isMissingNewColumnsError(error) {
  const msg = String(error?.message || error?.details || error?.hint || "");

  return (
    msg.includes("completed_lessons_today") ||
    msg.includes("column") ||
    msg.includes("schema cache")
  );
}

async function getLegacyStreakSummary(userId) {
  const today = getTodayDateString();

  const { data, error } = await supabase
    .from("user_streaks")
    .select("id, user_id, current_streak, longest_streak, last_activity_date")
    .eq("user_id", userId)
    .maybeSingle();

  if (error) throw error;

  if (!data) {
    return {
      user_id: userId,
      current_streak: 0,
      longest_streak: 0,
      last_activity_date: null,
      completed_lessons_today: [],
    };
  }

  if (!data.last_activity_date) {
    return {
      ...data,
      current_streak: 0,
      completed_lessons_today: [],
    };
  }

  const gap = diffDays(today, data.last_activity_date);

  if (gap > 1) {
    return {
      ...data,
      current_streak: 0,
      completed_lessons_today: [],
    };
  }

  return {
    ...data,
    completed_lessons_today: [],
  };
}

async function recordLegacyLevelCompletion(userId) {
  const today = getTodayDateString();

  const { data: existing, error: selectError } = await supabase
    .from("user_streaks")
    .select("id, user_id, current_streak, longest_streak, last_activity_date")
    .eq("user_id", userId)
    .maybeSingle();

  if (selectError) throw selectError;

  if (!existing) {
    const { data, error } = await supabase
      .from("user_streaks")
      .upsert(
        {
          user_id: userId,
          current_streak: 1,
          longest_streak: 1,
          last_activity_date: today,
        },
        { onConflict: "user_id" }
      )
      .select("id, user_id, current_streak, longest_streak, last_activity_date")
      .single();

    if (error) throw error;

    return {
      ...data,
      completed_lessons_today: [],
    };
  }

  const previousDate = existing.last_activity_date;
  const gap = previousDate ? diffDays(today, previousDate) : null;

  let currentStreak = Number(existing.current_streak || 0);
  let longestStreak = Number(existing.longest_streak || 0);

  if (!previousDate || gap > 1) {
    currentStreak = 0;
  }

  const nextCurrent = currentStreak + 1;
  const nextLongest = Math.max(longestStreak, nextCurrent);

  const { data, error } = await supabase
    .from("user_streaks")
    .update({
      current_streak: nextCurrent,
      longest_streak: nextLongest,
      last_activity_date: today,
    })
    .eq("user_id", userId)
    .select("id, user_id, current_streak, longest_streak, last_activity_date")
    .single();

  if (error) throw error;

  return {
    ...data,
    completed_lessons_today: [],
  };
}

export async function getCurrentUserStreakSummary() {
  const user = await getCurrentUser();
  const today = getTodayDateString();

  try {
    const { data, error } = await supabase
      .from("user_streaks")
      .select(
        "id, user_id, current_streak, longest_streak, last_activity_date, completed_lessons_today"
      )
      .eq("user_id", user.id)
      .maybeSingle();

    if (error) throw error;

    if (!data) {
      return {
        user_id: user.id,
        current_streak: 0,
        longest_streak: 0,
        last_activity_date: null,
        completed_lessons_today: [],
      };
    }

    const completedLessonsToday = normalizeCompletedLessons(
      data.completed_lessons_today
    );

    if (!data.last_activity_date) {
      return {
        ...data,
        current_streak: 0,
        completed_lessons_today: [],
      };
    }

    const gap = diffDays(today, data.last_activity_date);

    if (gap === 0) {
      return {
        ...data,
        completed_lessons_today: completedLessonsToday,
      };
    }

    if (gap === 1) {
      return {
        ...data,
        completed_lessons_today: [],
      };
    }

    return {
      ...data,
      current_streak: 0,
      completed_lessons_today: [],
    };
  } catch (error) {
    if (!isMissingNewColumnsError(error)) throw error;
    return getLegacyStreakSummary(user.id);
  }
}

export async function recordCurrentUserStreakActivity({
  lessonSlug = null,
  countLesson = false,
} = {}) {
  const user = await getCurrentUser();

  if (!countLesson) {
    return getCurrentUserStreakSummary();
  }

  const cleanLessonSlug =
    typeof lessonSlug === "string" && lessonSlug.trim().length > 0
      ? lessonSlug.trim()
      : null;

  if (!cleanLessonSlug) {
    return getCurrentUserStreakSummary();
  }

  const today = getTodayDateString();

  try {
    const { data: existing, error: selectError } = await supabase
      .from("user_streaks")
      .select(
        "id, user_id, current_streak, longest_streak, last_activity_date, completed_lessons_today"
      )
      .eq("user_id", user.id)
      .maybeSingle();

    if (selectError) throw selectError;

    if (!existing) {
      const completedLessonsToday = [cleanLessonSlug];

      const { data, error } = await supabase
        .from("user_streaks")
        .upsert(
          {
            user_id: user.id,
            current_streak: 1,
            longest_streak: 1,
            last_activity_date: today,
            completed_lessons_today: completedLessonsToday,
          },
          { onConflict: "user_id" }
        )
        .select(
          "id, user_id, current_streak, longest_streak, last_activity_date, completed_lessons_today"
        )
        .single();

      if (error) throw error;

      return {
        ...data,
        completed_lessons_today: normalizeCompletedLessons(
          data.completed_lessons_today
        ),
      };
    }

    const previousDate = existing.last_activity_date;
    const gap = previousDate ? diffDays(today, previousDate) : null;

    let currentStreak = Number(existing.current_streak || 0);
    let longestStreak = Number(existing.longest_streak || 0);
    let completedLessonsToday = normalizeCompletedLessons(
      existing.completed_lessons_today
    );

    if (!previousDate || gap > 1) {
      currentStreak = 0;
      completedLessonsToday = [];
    } else if (gap === 1) {
      completedLessonsToday = [];
    }

    if (
      previousDate === today &&
      completedLessonsToday.includes(cleanLessonSlug)
    ) {
      return {
        ...existing,
        completed_lessons_today: completedLessonsToday,
      };
    }

    const nextCurrent = currentStreak + 1;
    const nextLongest = Math.max(longestStreak, nextCurrent);
    const nextCompletedLessonsToday = [
      ...completedLessonsToday,
      cleanLessonSlug,
    ];

    const { data, error } = await supabase
      .from("user_streaks")
      .update({
        current_streak: nextCurrent,
        longest_streak: nextLongest,
        last_activity_date: today,
        completed_lessons_today: nextCompletedLessonsToday,
      })
      .eq("user_id", user.id)
      .select(
        "id, user_id, current_streak, longest_streak, last_activity_date, completed_lessons_today"
      )
      .single();

    if (error) throw error;

    return {
      ...data,
      completed_lessons_today: normalizeCompletedLessons(
        data.completed_lessons_today
      ),
    };
  } catch (error) {
    if (!isMissingNewColumnsError(error)) throw error;
    return recordLegacyLevelCompletion(user.id);
  }
}